import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { formatErr } from "@/lib/auth";
import { CheckCircle, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null); // { revoked: number } on success
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  // Redirect a few seconds after a successful reset, but clean the timer up
  // if the user navigates away (or clicks the Sign in CTA) first so we don't
  // fire nav() after unmount.
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => nav("/login"), 3500);
    return () => clearTimeout(t);
  }, [done, nav]);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 6) return setErr("Password must be at least 6 characters.");
    if (pw !== pw2) return setErr("Passwords don't match.");
    setLoading(true);
    try {
      const r = await api.post("/auth/reset-password", { token, new_password: pw });
      setDone({ revoked: r?.data?.sessions_revoked ?? 0 });
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] fl-noise flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-10 flex justify-center">
          <Logo variant="wordmark" size="lg" />
        </Link>
        <div className="fl-card p-8">
          {!token ? (
            <>
              <h1 className="fl-heading text-3xl">Choose a new password</h1>
              <p className="text-red-400 mt-4 text-sm" data-testid="rp-invalid">Invalid or missing reset token.</p>
            </>
          ) : done ? (
            <div className="text-center" data-testid="rp-success-card">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                <CheckCircle className="text-emerald-400" size={32} />
              </div>
              <h1 className="fl-heading text-2xl mt-5" data-testid="rp-success">Password updated</h1>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                Your new password is live.
              </p>
              <div className="mt-5 rounded-md border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3 text-left" data-testid="rp-signed-out-everywhere">
                <ShieldCheck size={18} className="text-[#f47a20] shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm text-zinc-100 font-medium">You've been signed out everywhere</div>
                  <div className="text-xs text-zinc-500 mt-1 leading-relaxed">
                    {done.revoked > 0
                      ? `We ended ${done.revoked} active ${done.revoked === 1 ? "session" : "sessions"} on other devices for your safety.`
                      : "Any other active sessions on this account have been ended."}
                    {" "}A confirmation email is on its way.
                  </div>
                </div>
              </div>
              <p className="text-xs text-zinc-500 mt-4">Taking you to sign in…</p>
              <Link to="/login" className="fl-btn mt-4 inline-flex w-full justify-center" data-testid="rp-signin">
                Sign in now
              </Link>
            </div>
          ) : (
            <>
              <h1 className="fl-heading text-3xl">Choose a new password</h1>
              <p className="text-xs text-zinc-500 mt-2">This link expires 30 minutes after it was sent and can only be used once.</p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <div className="fl-label mb-2">New password</div>
                  <input className="fl-input" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="rp-pw" />
                </div>
                <div>
                  <div className="fl-label mb-2">Confirm password</div>
                  <input className="fl-input" type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="rp-pw2" />
                </div>
                {err && <div className="text-sm text-red-400" data-testid="rp-error">{err}</div>}
                <button className="fl-btn w-full justify-center" disabled={loading} data-testid="rp-submit">{loading ? "Saving…" : "Reset password"}</button>
                <p className="text-[11px] text-zinc-500 text-center leading-relaxed">
                  For your safety we'll sign you out on every other device and email you a confirmation once the password is set.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
